import React from 'react';
import { Head } from '@components/common/head';
import { useI18NextContext } from '@components/hooks';
import { NavBarContainer } from '@components/navigation';
import { ComponentContainer, SectionSeparator } from '@styles/layout';
import { PageTitle } from '@components/common/page-title';
import { Footer } from '@components/footer';
import { useTheme, withTheme } from '@emotion/react';
import { Theme } from '@theme/base';
import { Error404 } from '@components/common/hero/error';
import { PageProps } from '@model/pages';
import { Region, Country, Tour, GeographyType } from '@model/common';
import { SitemapTripTypes } from '@components/sitemap/SitemapTripTypes';
import { SitemapTours } from '@components/sitemap/SitemapTours';
import { SitemapHolidays } from '@components/sitemap/SitemapHolidays';
import SearchTabsBar from '@components/search/SearchTabsBar';
import { ERROR_CODES } from '@model/common/error-codes';
import { hasHolidays, getTripTypes } from '@util/geography';

export interface SitemapDestinationPageLayoutProps extends PageProps {
  destination?: Region | Country;
  tours?: Array<Tour>;
}

const SitemapDestinationPageLayout = ({ destination, promoBannerTripType }: SitemapDestinationPageLayoutProps) => {
  const theme: Theme = useTheme();
  const t: any = useI18NextContext();
  if (!destination) return <Error404 code={ERROR_CODES.NO_API_DESTINATION} />;

  const { display, type } = destination;

  const tours = type === GeographyType.COUNTRY ? destination.tours : undefined;
  const hasTours = !!tours && !!tours.length;
  const tripTypes = getTripTypes(destination);

  return (
    <>
      <Head
        metaTitle={`${display} ${t('sitemap__home--title')} | ${t('common__brand_display')}`}
        metaDescription={t('sitemap__home-meta-description', { destination: display })}
      />
      <NavBarContainer promoBannerTripType={promoBannerTripType} renderSearch={<SearchTabsBar condensed />} />
      <ComponentContainer>
        <SectionSeparator size={theme.custom.spacing.xxxLarge} />
        <PageTitle heading={display} boldHeading />
        <SectionSeparator size={theme.custom.spacing.xLarge} />
        {hasHolidays(destination) && (
          <>
            <SitemapHolidays destination={destination} />
            <SectionSeparator size={theme.custom.spacing.xLarge} />
          </>
        )}
        <SitemapTripTypes destination={destination} tripTypes={tripTypes} />
        <SectionSeparator size={theme.custom.spacing.xLarge} />
        {hasTours && (
          <>
            <SitemapTours destination={destination} tours={tours} />
            <SectionSeparator size={theme.custom.spacing.xLarge} />
          </>
        )}
      </ComponentContainer>
      <SectionSeparator size={theme.custom.spacing.xxxLarge} />
      <Footer />
    </>
  );
};

export default withTheme(SitemapDestinationPageLayout);
